<script lang="ts">
    import { spaLink } from "../../../../functionTools/navigation"
    import MedialibImage from "../../../widgets/MedialibImage.svelte"
    import MediaQuery from "../../../widgets/MediaQuery.svelte"
    import MobileImageLink from "./MobileImageLink.svelte"
    import MobileAdditionals from "./MobileAdditionals.svelte"

    let {
        subNavEl,
        closeMenu,
        forceClose,
        activeMobileSubnavPath = $bindable<string | null>(null),
    }: {
        subNavEl: NavigationNode
        closeMenu: () => void
        forceClose: () => void
        activeMobileSubnavPath?: string | null
    } = $props()
    let showMobileSubnav = $state(false)

    $effect(() => {
        if (showMobileSubnav) {
            activeMobileSubnavPath = subNavEl.path
            return
        }

        if (activeMobileSubnavPath === subNavEl.path) activeMobileSubnavPath = null
    })

    const onClick = (e: MouseEvent) => {
        showMobileSubnav = false
        activeMobileSubnavPath = null
        closeMenu()
        forceClose()
        e.preventDefault()
    }
</script>

<li class="nav-item" class:active-mobile-subnav={showMobileSubnav}>
    <MediaQuery query={"(min-width: 1600px)"}>
        {#snippet contentSnippet()}
            <div class="parent-nav main">
                {#if subNavEl.external}
                    <a
                        class="inner-wrapper"
                        href={subNavEl.externalUrl}
                        target={subNavEl.target || "_self"}
                        rel={subNavEl.target === "_blank" ? "noopener noreferrer" : undefined}
                        onclick={(e) => {
                            closeMenu()
                            forceClose()
                            e.preventDefault()
                        }}
                    >
                        {#if subNavEl.backgroundImage}
                            <div class="background-image-wrapper" aria-hidden="true">
                                <MedialibImage id={subNavEl.backgroundImage} widthMultiplier={2} />
                            </div>
                        {/if}
                        <div class="image-wrapper" class:has-background={!!subNavEl.backgroundImage} aria-hidden="true">
                            <MedialibImage id={subNavEl.image} widthMultiplier={2} />
                        </div>
                        <span>
                            {subNavEl.name}
                        </span>
                    </a>
                {:else}
                    <a
                        class="inner-wrapper"
                        href={subNavEl.path || "#"}
                        onclick={(e) => {
                            closeMenu()
                            forceClose()
                            e.preventDefault()
                        }}
                        use:spaLink
                    >
                        {#if subNavEl.backgroundImage}
                            <div class="background-image-wrapper" aria-hidden="true">
                                <MedialibImage id={subNavEl.backgroundImage} widthMultiplier={2} />
                            </div>
                        {/if}
                        <div class="image-wrapper" class:has-background={!!subNavEl.backgroundImage} aria-hidden="true">
                            <MedialibImage id={subNavEl.image} widthMultiplier={2} />
                        </div>
                        <span>
                            {subNavEl.name}
                        </span>
                    </a>
                {/if}
            </div>
        {/snippet}
        {#snippet altSnippet()}
            <MobileImageLink {subNavEl} bind:showMobileSubnav {closeMenu} />
        {/snippet}
    </MediaQuery>
    <ul class="sub-nav" class:showMobileSubnav>
        <MobileAdditionals
            {closeMenu}
            bind:mobileSubnavOpen={showMobileSubnav}
            path={subNavEl.path}
            title={subNavEl.name}
        />
        {#each (subNavEl.nodes || []).filter((node) => !node.leftMenuOnly) as element, i}
            <li class="parent-nav">
                {#if element.external}
                    <a
                        href={element.externalUrl}
                        target={element.target || "_self"}
                        rel={element.target === "_blank" ? "noopener noreferrer" : undefined}
                    >
                        {element.name}
                    </a>
                {:else}
                    <a
                        href={element.path}
                        use:spaLink={{
                            onClick,
                        }}
                    >
                        {element.name}
                    </a>
                {/if}
            </li>
        {/each}
    </ul>
</li>

<style lang="less" global>
    @import "../../../../css/variables.less";
    .nav-item {
        display: flex;
        flex-direction: column;
        gap: 0.6rem;
        flex-grow: 1;
        max-width: 300px;

        a {
            color: var(--grey-400);
        }

        .parent-nav {
            font-size: var(--bodyfontsize);
        }
        .parent-nav.main {
            font-weight: 700;
            width: 100%;
            border-bottom: 1px solid var(--grey-300);
            font-size: 1rem;
            .inner-wrapper {
                display: flex;
                flex-direction: column;
                gap: 0.6rem;
                position: relative;
                transition: transform 0.3s ease !important;
                &:hover {
                    transform: translateY(-0.6rem) !important;
                }
                .image-wrapper {
                    width: 100%;
                    height: 192px;
                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        border-radius: 4px;
                    }
                    &.has-background {
                        padding: 0.6rem;
                        z-index: 10;
                        position: relative;
                        img {
                            height: 100%;
                            border-radius: 0px;
                            width: 100%;
                            object-fit: contain;
                        }
                    }
                }

                .background-image-wrapper {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 192px;
                    z-index: 1;
                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        border-radius: 4px;
                    }
                }
            }
        }

        .sub-nav {
            display: flex;
            flex-direction: column;
            gap: 0.3rem;
            margin: 0;
            padding-left: 0;
            list-style: none;
        }
        @media @header_mobile {
            max-width: unset;
            width: 100%;
            flex-grow: unset;
            &.active-mobile-subnav > :not(.sub-nav) {
                display: none;
            }
            .sub-nav {
                position: absolute;
                top: 0%;
                left: -100vw;
                width: 100%;
                transition: left 0.5s ease;
                justify-content: flex-start;
                display: flex;
                flex-direction: column;
                padding: 12px 0px;
                max-width: var(--max-container-width);
                gap: 0rem;
                z-index: 100;
                &.showMobileSubnav {
                    left: 0px;
                }
            }
        }
    }
</style>
